import { css } from 'lit';
import { ConfigDMS } from '@/config/ConfigDMS';
import { FRAME_FIELD_NAMES } from '@/constants';
import { CustomMessageDto, DMSSignType, MessageDto, QueueState, SignDto } from '@typings/api';
import { isValidString } from '@/utils/utils';
import { frameTextToString } from '@/components/common/dms-frame';
import ConfigVSL from '@/config/ConfigVSL';
import { isValidNumber } from './utils';

export function signHasMessage(sign: SignDto) {
	const typeConfig = ConfigDMS.Map.icons[sign.type];

	if (sign.type === DMSSignType.VSL) {
		return (
			typeConfig &&
			sign.status === 'ACTIVE' &&
			sign.displayedMessageDto?.frame1?.text !== undefined &&
			sign.displayedMessageDto?.frame1?.text !== null &&
			sign.displayedMessageDto?.frame1?.text !== '' &&
			sign.displayedMessageDto?.frame1?.text !== '[nl]' &&
			sign.displayedMessageDto?.frame1?.text !== '[jp3][jl3]' &&
			sign.displayedMessageDto?.frame1?.text !== '[jp3][jl3][nl]'
		);
	}
	return (
		typeConfig &&
		sign.status === 'ACTIVE' &&
		sign.messageCount !== undefined &&
		sign.messageCount > 0
	);
}

export function signHasNoMessage(sign: SignDto) {
	const typeConfig = ConfigDMS.Map.icons[sign.type];
	if (sign.type === DMSSignType.VSL) {
		return (
			typeConfig &&
			sign.status === 'ACTIVE' &&
			(sign.displayedMessageDto?.frame1?.text === undefined ||
				sign.displayedMessageDto?.frame1?.text === null ||
				sign.displayedMessageDto?.frame1?.text === '' ||
				sign.displayedMessageDto?.frame1?.text === '[jp3][jl3]' ||
				sign.displayedMessageDto?.frame1?.text === '[jp3][jl3][nl]' ||
				sign.displayedMessageDto?.frame1?.text === '[nl]')
		);
	}
	return typeConfig && sign.status === 'ACTIVE' && sign.messageCount === 0;
}

export const messageHasGraphicFrame = (message: MessageDto): boolean => {
	return FRAME_FIELD_NAMES.some((field) => isValidNumber(message[field]?.graphicId));
};

export const messageIsSupportedBySign = (message?: CustomMessageDto, sign?: SignDto): boolean => {
	if (!sign || !message) {
		return false;
	}

	switch (sign.type) {
		case DMSSignType.DMS:
			if (sign.fullMatrix) {
				return !message.shortMessage ?? false;
			}
			if (!sign.fullMatrix) {
				return !messageHasGraphicFrame(message) && !message.shortMessage;
			}
			return true;
		case DMSSignType.PDMS:
			if (sign.fullMatrix) {
				return messageHasGraphicFrame(message) || (message.shortMessage ?? false);
			}
			if (!sign.fullMatrix) {
				return !messageHasGraphicFrame(message) && (message.shortMessage ?? false);
			}
			return true;
		default:
			return true;
	}
};

export const formatTimeUntilMessageExpires = (futureTime: number): string => {
	const now = new Date();
	const future = new Date(futureTime);
	const diffMs = future.getTime() - now.getTime();
	if (diffMs <= 0) return 'Now';
	const diffSeconds = Math.floor(diffMs / 1000);
	const diffMinutes = Math.floor(diffSeconds / 60);
	const diffHours = Math.floor(diffMinutes / 60);
	const diffDays = Math.floor(diffHours / 24);
	const diffMonths = Math.floor(diffDays / 30);
	const diffYears = Math.floor(diffDays / 365);
	if (diffYears >= 1) {
		return `${diffYears}+ years`;
	}
	if (diffMonths >= 2) {
		return `${diffMonths} months`;
	}
	if (diffMonths === 1) {
		return '1 month';
	}
	if (diffDays >= 2) {
		return `${diffDays} days`;
	}
	if (diffHours >= 12) {
		return diffDays === 1 ? '1 day' : `${diffHours} hours`;
	}
	if (diffMinutes >= 60) {
		return `${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
	}
	return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''}`;
};

export const speedLimitIcon = (sign: SignDto): string => {
	if (sign.displayedMessageDto?.frame1 == null) {
		return '';
	}

	const speed = frameTextToString(sign.displayedMessageDto.frame1.text).trim();

	if (speed === ConfigVSL.standbyText || !isValidString(speed)) {
		return 'img/speed-limit-standby.svg';
	}

	return `img/speed-limit-${speed}.svg`;
};

export const signHasMessageError = (sign: SignDto) =>
	isValidString(sign.ipAddress) &&
	(sign.currentMessageState === QueueState.ERROR || sign.currentMessageState === QueueState.UNSENT);
